import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Tag, 
  Space, 
  Modal, 
  Row,
  Col,
  Statistic,
  Dropdown,
  Avatar,
  message,
  Popconfirm,
  Spin
} from 'antd';
import { 
  DeleteOutlined,
  EyeOutlined,
  LogoutOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface Teacher {
  id: string;
  username: string;
  name: string;
  email: string;
  role: string;
  test_count: number;
  student_count: number;
  created_at: string;
}

interface Student {
  id: string;
  name: string;
  email: string;
  student_id: string;
  class_name: string;
  tests_taken: number;
  avg_score: number;
  created_at: string;
}

interface Test {
  id: string;
  title: string;
  description: string;
  question_count: number;
  submission_count: number;
  created_at: string;
}

interface Stats {
  teacherCount: number;
  studentCount: number;
  testCount: number;
  completedTestsCount: number;
}

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [teacher, setTeacher] = useState<any>(null);
  
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [teacherStudents, setTeacherStudents] = useState<Student[]>([]);
  const [teacherTests, setTeacherTests] = useState<Test[]>([]);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showTestsModal, setShowTestsModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const teacherData = localStorage.getItem('teacher');
    
    if (!token || !teacherData) {
      navigate('/teacher/login');
      return;
    }

    const parsedTeacher = JSON.parse(teacherData);
    if (parsedTeacher.role !== 'super_admin') {
      navigate('/teacher');
      return;
    }

    setTeacher(parsedTeacher);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    fetchStats();
    fetchTeachers();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/stats');
      setStats(response.data);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    }
  };

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/teachers');
      setTeachers(response.data);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        message.error('Failed to fetch teachers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('teacher');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/teacher/login');
    message.success('Logged out');
  };

  const handleViewStudents = async (record: Teacher) => {
    setSelectedTeacher(record);
    setDetailLoading(true);
    setShowStudentsModal(true);
    
    try {
      const response = await axios.get(`/api/admin/teachers/${record.id}/students`);
      setTeacherStudents(response.data);
    } catch (error) {
      message.error('Failed to fetch students');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewTests = async (record: Teacher) => {
    setSelectedTeacher(record);
    setDetailLoading(true);
    setShowTestsModal(true);
    
    try {
      const response = await axios.get(`/api/admin/teachers/${record.id}/tests`);
      setTeacherTests(response.data);
    } catch (error) {
      message.error('Failed to fetch tests');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDeleteTeacher = async (teacherId: string) => {
    try {
      await axios.delete(`/api/admin/teachers/${teacherId}`);
      message.success('Teacher and all resources deleted successfully');
      fetchTeachers();
      fetchStats();
    } catch (error) {
      message.error('Failed to delete teacher');
    }
  };

  const teacherColumns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => name || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: 'Tests',
      dataIndex: 'test_count',
      key: 'test_count',
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Students',
      dataIndex: 'student_count',
      key: 'student_count',
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_: any, record: Teacher) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<TeamOutlined />}
            onClick={() => handleViewStudents(record)}
          >
            Students
          </Button>
          <Button 
            type="link" 
            icon={<FileTextOutlined />}
            onClick={() => handleViewTests(record)}
          >
            Tests
          </Button>
          <Popconfirm
            title="Delete Teacher"
            description={
              <div>
                <p>Are you sure you want to delete this teacher?</p>
                <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                  This will also delete all their tests, questions, and student answers!
                </p>
              </div>
            }
            onConfirm={() => handleDeleteTeacher(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const studentColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Student ID',
      dataIndex: 'student_id',
      key: 'student_id',
      render: (id: string) => id || '-',
    },
    {
      title: 'Class',
      dataIndex: 'class_name',
      key: 'class_name',
      render: (c: string) => c || '-',
    },
    {
      title: 'Tests Taken',
      dataIndex: 'tests_taken',
      key: 'tests_taken',
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Avg Score',
      dataIndex: 'avg_score',
      key: 'avg_score',
      render: (score: number) => score ? `${score}%` : '-',
    },
  ];

  const testColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Questions',
      dataIndex: 'question_count',
      key: 'question_count',
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Submissions',
      dataIndex: 'submission_count',
      key: 'submission_count',
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_: any, record: Test) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={() => window.open(`/statistics/${record.id}`, '_blank')}
        >
          View Stats
        </Button>
      ),
    },
  ];

  const userMenuItems = [
    {
      key: 'teacher-dashboard',
      label: (
        <span onClick={() => navigate('/teacher')}>
          <FileTextOutlined /> Teacher Dashboard
        </span>
      )
    },
    {
      key: 'logout',
      label: (
        <span onClick={handleLogout}>
          <LogoutOutlined /> Logout
        </span>
      )
    }
  ];

  return (
    <div>
      <div className="header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ color: 'white' }}>Super Admin Dashboard</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)' }}>Manage all teachers and view system statistics</p>
          </div>
          {teacher && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '8px 16px',
                borderRadius: '4px',
                background: 'rgba(255,255,255,0.2)'
              }}>
                <Avatar icon={<UserOutlined />} style={{ marginRight: '8px', background: '#fff', color: '#667eea' }} />
                <span style={{ color: 'white' }}>{teacher.name || teacher.username}</span>
                <Tag color="gold" style={{ marginLeft: '8px' }}>Super Admin</Tag>
              </div>
            </Dropdown>
          )}
        </div>
      </div>
      
      <div className="container">
        {stats && (
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Teachers"
                  value={stats.teacherCount}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Students"
                  value={stats.studentCount}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Tests"
                  value={stats.testCount}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Completed Exams"
                  value={stats.completedTestsCount}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        <Card>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>All Teachers</h2>
            <Button onClick={fetchTeachers} loading={loading}>
              Refresh
            </Button>
          </div>
          
          <Table
            columns={teacherColumns}
            dataSource={teachers}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </Card>

        <Modal
          title={`Students of ${selectedTeacher?.name || selectedTeacher?.username}`}
          open={showStudentsModal}
          onCancel={() => {
            setShowStudentsModal(false);
            setTeacherStudents([]);
          }}
          footer={null}
          width={800}
        >
          {detailLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : teacherStudents.length > 0 ? (
            <Table
              columns={studentColumns}
              dataSource={teacherStudents}
              rowKey="id"
              pagination={{ pageSize: 5 }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              No students have taken tests from this teacher yet
            </div>
          )}
        </Modal>

        <Modal
          title={`Tests by ${selectedTeacher?.name || selectedTeacher?.username}`}
          open={showTestsModal}
          onCancel={() => {
            setShowTestsModal(false);
            setTeacherTests([]);
          }}
          footer={null}
          width={800}
        >
          {detailLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : teacherTests.length > 0 ? (
            <Table
              columns={testColumns}
              dataSource={teacherTests}
              rowKey="id"
              pagination={{ pageSize: 5 }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              This teacher has not created any tests yet
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
